import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useLocation, Link } from "react-router-dom";
import { MapPinIcon, ArrowTopRightOnSquareIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import Map, { Marker } from 'react-map-gl/mapbox';
import PropertyReformDetails from "../components/PropertyReformDetails";
import EstadoGeneralThermometer from "../components/EstadoGeneralThermometer";
import IAToggleButton from "../components/IAToggleButton";
import { formatCurrency } from "../utils/investmentIndicatorsHelper";
import 'mapbox-gl/dist/mapbox-gl.css';

const TIPO_LABELS = {
    sin_reforma: 'Sin reforma',
    estandar: 'Reforma Estándar',
    premium: 'Reforma Premium',
    vacacional: 'Alquiler Vacacional',
};

const PERFIL_LABELS = {
    propietario: "Propietario",
    comprador: "Comprador",
    inversor: "Inversor",
    agente: "Agente inmobiliario",
    constructor: "Constructor",
};

// ─── Helpers ──────────────────────────────────────────────────

const ACTUACION_EXPLICACIONES = {
    pintura: "Bajo coste con gran transformación visual. Paredes frescas y luminosas hacen que los espacios parezcan más amplios y modernos. Es lo primero que ve un comprador en fotos y visitas.",
    gotel: "El gotelé es sinónimo de vivienda anticuada en España. Eliminarlo moderniza instantáneamente la percepción del inmueble y es una de las actuaciones con mejor relación coste-impacto.",
    tarima: "El suelo es visible en todas las estancias y en todas las fotos del anuncio. Sustituir terrazo o baldosa antigua por tarima flotante transforma completamente la imagen de la vivienda.",
    alicatado: "Cocinas y baños son los espacios que más escrutinio reciben de los compradores. Un alicatado nuevo transmite reforma integral y calidad.",
    sanitario: "Sanitarios antiguos o amarillentos son un rechazo inmediato para compradores. Su sustitución tiene alto impacto visual y funcional a coste moderado.",
    ventana: "Las ventanas nuevas son visibles desde dentro y fuera, mejoran el aislamiento acústico y térmico, y elevan la calificación energética. Los compradores valoran cada vez más la eficiencia.",
    foco: "La iluminación empotrada y LED crea un ambiente premium que fotografía muy bien. Bajo coste con efecto \"vivienda de diseño\" que diferencia la propiedad en el mercado.",
    led: "La iluminación empotrada y LED crea un ambiente premium que fotografía muy bien. Bajo coste con efecto \"vivienda de diseño\" que diferencia la propiedad en el mercado.",
    puerta: "Puertas nuevas (especialmente lacadas en blanco) modernizan notablemente el recorrido por la vivienda. El comprador las percibe al caminar por cada estancia.",
    "aire acondicionado": "Un diferenciador real en el sur de España. El sistema por conductos es más estético que los splits y se percibe como acabado premium, aunque su coste es elevado.",
    "suelo radiante": "Tendencia en alza: las viviendas nuevas ya lo incluyen de serie. Ofrece calefacción y refrigeración eficiente, elimina radiadores y libera espacio para mobiliario. Cada vez más demandado.",
    tuber: "Las tuberías nuevas son esenciales pero están ocultas tras las paredes. No se pueden fotografiar ni apreciar en una visita, por lo que su impacto en el valor percibido es bajo.",
    "instalaci": "El cableado eléctrico es invisible y el comprador lo da por hecho en cualquier reforma integral. Necesario por seguridad, pero no genera impacto emocional ni visual.",
};

function getExplicacionActuacion(nombre) {
    if (!nombre) return null;
    const lower = nombre.toLowerCase();
    for (const [clave, texto] of Object.entries(ACTUACION_EXPLICACIONES)) {
        if (lower.includes(clave)) return texto;
    }
    return null;
}

// ─── Component ────────────────────────────────────────────────

const PropertyReport = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const vieneDeMisInformes = location.state?.from === 'mis-informes';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [propData, setPropData] = useState(null);   // GET /propiedades/:id
    const [informe, setInforme] = useState(null);     // GET /api/v2/propiedades/:id/informe
    const [analisisIA, setAnalisisIA] = useState(null); // GET /api/v2/propiedades/:id/analisis-ia
    const [imagenes, setImagenes] = useState([]);
    const [comparables, setComparables] = useState([]);
    const [reformaParcialROI, setReformaParcialROI] = useState(null);
    const [roiExpanded, setRoiExpanded] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [numericId, setNumericId] = useState(null);
    const [soloIA, setSoloIA] = useState(false);

    // Resolve UUID → numeric id
    useEffect(() => {
        const resolve = async () => {
            const isUuid = /^[a-f0-9-]{36}$/.test(id);
            if (!isUuid) { setNumericId(id); return; }
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/by-public-id/${id}`, { credentials: "include" });
                if (res.ok) { setNumericId((await res.json()).id); }
                else { setError("Propiedad no encontrada"); setLoading(false); }
            } catch {
                setError("Error al resolver la propiedad"); setLoading(false);
            }
        };
        resolve();
    }, [id]);

    // Fetch los tres endpoints en paralelo
    const fetchReport = useCallback(async () => {
        if (!numericId) return;
        try {
            const token = searchParams.get("token");
            const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";

            const [propRes, informeRes, iaRes, sugerRes, imagenesRes, comparablesRes, actRes] = await Promise.all([
                fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${numericId}`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/informe${tokenParam}`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/analisis-ia${tokenParam}`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/sugerencias-ia`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/imagenes`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/comparables`, { credentials: "include" }),
                fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${numericId}/reforma-parcial-roi`, { credentials: "include" }),
            ]);

            if (!propRes.ok) throw new Error("Propiedad no encontrada");
            if (!informeRes.ok) throw new Error("Error al cargar el informe de inversión");

            const [prop, inf, ia, suger] = await Promise.all([
                propRes.json(),
                informeRes.json(),
                iaRes.ok ? iaRes.json() : { analisis: null },
                sugerRes.ok ? sugerRes.json() : { sugerencias: {} },
            ]);

            // Enrich reformas with IA suggestions for Modo IA toggle
            const sugerencias = suger.sugerencias ?? {};
            const reformasEnriquecidas = (prop.reformas ?? []).map(reforma => {
                const tipoIA = reforma.tipo_reforma?.includes('Estándar') ? 'Reforma Estándar IA'
                             : reforma.tipo_reforma?.includes('Premium') ? 'Reforma Premium IA'
                             : null;
                const mapaIA = tipoIA ? (sugerencias[tipoIA] ?? {}) : {};
                return {
                    ...reforma,
                    actuaciones: (reforma.actuaciones ?? []).map(act => {
                        const key = (act.actuacion ?? '').toLowerCase();
                        const cantidadIA = mapaIA[key];
                        return { ...act, sugeridoIA: cantidadIA != null, cantidadSugeridaIA: cantidadIA ?? null };
                    }),
                };
            });

            setPropData({ ...prop, reformas: reformasEnriquecidas, precio: Number(prop.precio), superficie: Number(prop.superficie) });
            setInforme(inf);
            setAnalisisIA(ia?.analisis ?? null);
            setImagenes(imagenesRes.ok ? ((await imagenesRes.json()).imagenes ?? []) : []);
            setComparables(comparablesRes.ok ? ((await comparablesRes.json()).comparables ?? []) : []);
            setReformaParcialROI(actRes.ok ? ((await actRes.json()).reformaParcialROI ?? null) : null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [numericId, searchParams]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    // Scroll al anchor del hash una vez que los datos están cargados
    useEffect(() => {
        if (loading) return;
        const hash = location.hash.replace('#', '');
        if (!hash) return;
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [loading, location.hash]);

    // ─── Loading / error states ───────────────────────────────

    if (loading) return (
        <div className="container mx-auto px-4 py-16 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-6" />
            <h2 className="text-xl font-semibold text-gray-700">Cargando informe...</h2>
        </div>
    );

    if (error) return (
        <div className="container mx-auto px-4 py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-500">{error}</p>
        </div>
    );

    if (!propData || !informe) return null;

    // ─── Datos derivados ──────────────────────────────────────

    const { escenarios, mercado } = informe;
    const { precio, superficie } = propData;
    const precioM2 = superficie ? Math.round(precio / superficie) : null;

    const sinReforma = escenarios.find(e => e.tipoReforma === 'sin_reforma');
    const precioSinReforma = sinReforma?.venta?.precioVentaEstimado ?? null;

    // El backend devuelve escenariosIA cuando hay análisis IA; el frontend solo elige cuál mostrar
    const hasIA = !!informe.escenariosIA;
    const escenariosActivos = soloIA && hasIA ? informe.escenariosIA : escenarios;
    // Lookup del escenario original (para tachados cuando soloIA está activo)
    const origByTipo = Object.fromEntries(escenarios.map(e => [e.tipoReforma, e]));
    const iaToggle = hasIA ? () => setSoloIA(v => !v) : null;

    // Escenarios con coste de reforma (excluye sin_reforma)
    const escenariosConReforma = escenariosActivos.filter(e => e.tipoReforma !== 'sin_reforma' && e.costoReforma > 0);

    // Mejor escenario: mayor ROI a 1 año
    const mejorEscenario = [...escenariosConReforma]
        .filter(e => e.venta?.roi?.anio1 != null)
        .sort((a, b) => b.venta.roi.anio1 - a.venta.roi.anio1)[0] ?? null;

    // ─── Render ───────────────────────────────────────────────

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">

            {/* Header */}
            <div className="mb-6">
                {vieneDeMisInformes && (
                    <Link
                        to="/mis-informes"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mb-3"
                    >
                        ← Volver a Mis Informes
                    </Link>
                )}
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    Informe de análisis de reforma
                </h1>
                {propData.direccion && (
                    <p className="text-gray-700 font-medium">{propData.direccion}</p>
                )}
                <p className="text-gray-500 text-sm mt-0.5">
                    {[propData.municipio, propData.distrito].filter(Boolean).join(" · ")}
                    {propData.perfil_solicitante && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {PERFIL_LABELS[propData.perfil_solicitante] || propData.perfil_solicitante}
                        </span>
                    )}
                </p>
                {propData.detalle_url && (
                    <a
                        href={propData.detalle_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors group"
                    >
                        <span className="text-gray-400 group-hover:text-gray-600">Fuente:</span>
                        <span className="font-medium">
                            {propData.detalle_url.includes('idealista.com') ? 'Idealista' : 'Anuncio original'}
                        </span>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" />
                    </a>
                )}
            </div>

            {/* Mapa */}
            {propData.latitud && propData.longitud && (
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-6" style={{ height: 260 }}>
                    <Map
                        mapboxAccessToken={window.MAPBOX_TOKEN}
                        initialViewState={{ longitude: propData.longitud, latitude: propData.latitud, zoom: 15 }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                        style={{ width: '100%', height: '100%' }}
                        interactive={false}
                    >
                        <Marker longitude={propData.longitud} latitude={propData.latitud} anchor="bottom">
                            <MapPinIcon className="h-8 w-8 text-blue-600 drop-shadow" />
                        </Marker>
                    </Map>
                </div>
            )}

            {/* Verdict Card */}
            <VerdictCard precio={precio} mejorEscenario={mejorEscenario} precioSinReforma={precioSinReforma} />

            {/* ══ STAGE 1: Tu propiedad hoy ══ */}
            <StageDivider number={1} title="Tu propiedad hoy" subtitle="Valor actual y posición en el mercado" />

            {/* Detalles */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de la propiedad</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <DetailItem label="Precio" value={formatCurrency(precio)} />
                    <DetailItem label="Superficie" value={superficie ? `${superficie} m²` : "N/D"} />
                    <DetailItem label="Habitaciones" value={propData.habitaciones ?? "N/D"} />
                    <DetailItem label="Baños" value={propData.banos ?? "N/D"} />
                    <DetailItem label="Municipio" value={propData.municipio || "N/D"} />
                    <DetailItem label="Distrito" value={propData.distrito || "N/D"} />
                    {propData.direccion && <DetailItem label="Dirección" value={propData.direccion} />}
                    {precioM2 && <DetailItem label="Precio/m²" value={`${formatCurrency(precioM2)}/m²`} />}
                </div>
                {propData.descripcion && (
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Descripción</h4>
                        <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{propData.descripcion}</p>
                    </div>
                )}
            </div>

            {/* Datos Catastrales */}
            {propData.datos_catastrales && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos Catastrales</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Referencia catastral</p>
                            <p className="text-sm font-medium font-mono text-gray-900 mt-1">{propData.datos_catastrales.referencia_catastral}</p>
                            <div className="flex gap-3 mt-1">
                                <button
                                    onClick={() => navigator.clipboard.writeText(propData.datos_catastrales.referencia_catastral)}
                                    className="text-xs text-blue-600 hover:underline"
                                >
                                    Copiar
                                </button>
                                {propData.datos_catastrales.codigo_del && propData.datos_catastrales.codigo_mun ? (
                                    <a
                                        href={`https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCConCiud.aspx?del=${propData.datos_catastrales.codigo_del}&mun=${propData.datos_catastrales.codigo_mun}&RefC=${propData.datos_catastrales.referencia_catastral}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        Ver en SEDECatastro ↗
                                    </a>
                                ) : (
                                    <a href="https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCBusqueda.aspx" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:underline">SEDECatastro ↗</a>
                                )}
                            </div>
                        </div>
                        {propData.datos_catastrales.superficie_catastral && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Superficie oficial</p>
                                <p className="text-sm font-medium text-gray-900 mt-1">
                                    {propData.datos_catastrales.superficie_catastral} m²
                                    {superficie && Math.abs(propData.datos_catastrales.superficie_catastral - superficie) / superficie > 0.05 && (
                                        <span className="ml-1 text-yellow-600 text-xs">⚠️ Difiere de {superficie} m²</span>
                                    )}
                                </p>
                            </div>
                        )}
                        {propData.datos_catastrales.anno_construccion && (
                            <DetailItem label="Año construcción" value={propData.datos_catastrales.anno_construccion} />
                        )}
                        {propData.datos_catastrales.codigo_postal && (
                            <DetailItem label="Código postal" value={propData.datos_catastrales.codigo_postal} />
                        )}
                        {propData.datos_catastrales.uso_principal && (
                            <DetailItem label="Uso principal" value={propData.datos_catastrales.uso_principal} />
                        )}
                    </div>
                    <p className="text-xs text-gray-400">Fuente: Sede Electrónica del Catastro (Hacienda)</p>
                </div>
            )}

            {/* Propiedades similares en la zona */}
            {comparables.length > 0 && (
                <div id="comparables" className="bg-white rounded-xl border border-gray-200 p-6 mb-8 scroll-mt-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Propiedades similares en la zona</h3>
                    <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2">
                        {comparables.map(comp => {
                            const deltaPrecioM2 = precioM2 && comp.precioM2 ? comp.precioM2 - precioM2 : null;
                            return (
                                <div key={comp.id} className="rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 w-64">
                                    {comp.imagenUrl && (
                                        <div className="aspect-[16/9] bg-gray-100">
                                            <img src={comp.imagenUrl} alt={comp.titulo} className="w-full h-full object-cover" loading="lazy" />
                                        </div>
                                    )}
                                    <div className="p-4">
                                        <p className="font-medium text-gray-900 text-sm truncate">{(comp.titulo && comp.titulo !== "Sin título") ? comp.titulo : "Propiedad en la zona"}</p>
                                        <p className="text-lg font-bold text-gray-900">{formatCurrency(comp.precio)}</p>
                                        <p className="text-xs text-gray-500">
                                            {comp.superficie} m² · {comp.habitaciones > 0 ? `${comp.habitaciones} hab` : "N/D"} · {formatCurrency(comp.precioM2)}/m²
                                        </p>
                                        {deltaPrecioM2 !== null && (
                                            <p className={`text-xs mt-1 font-medium ${deltaPrecioM2 > 0 ? "text-red-500" : "text-green-600"}`}>
                                                {deltaPrecioM2 > 0 ? "+" : ""}{formatCurrency(deltaPrecioM2)}/m² vs tu propiedad
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Precio vs mercado — solo si hay precio de mercado sin reformar */}
            {precioSinReforma != null && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Precio pedido vs valor de mercado</h3>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Precio pedido</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(precio)}</p>
                        </div>
                        <div className="text-gray-300 hidden sm:block"><span className="text-lg">›</span></div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">
                                Valor estimado zona · {mercado.precioM2SinReformar ? `${formatCurrency(mercado.precioM2SinReformar)}/m²` : ''}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(precioSinReforma)}</p>
                        </div>
                        {(() => {
                            const delta = precioSinReforma - precio;
                            const pct = Math.round(delta / precio * 100);
                            return (
                                <div className={`px-4 py-2 rounded-xl ${delta > 0 ? "bg-green-50" : delta < 0 ? "bg-red-50" : "bg-gray-50"}`}>
                                    <p className={`text-lg font-bold ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-gray-500"}`}>
                                        {delta > 0 ? "+" : ""}{formatCurrency(delta)}
                                    </p>
                                    <p className="text-xs text-gray-500">{pct > 0 ? "+" : ""}{pct}% vs precio pedido</p>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Análisis IA — estado de la propiedad */}
            {analisisIA && (
                <div id="analisis-ia" className="bg-white rounded-xl border border-blue-200 p-6 mb-8 scroll-mt-20">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">Análisis IA</span>
                        <h3 className="text-lg font-semibold text-gray-900">Estado de la propiedad</h3>
                    </div>
                    <EstadoGeneralThermometer estadoGeneral={
                        analisisIA.resumen?.estadoGeneral != null
                            ? Math.round(analisisIA.resumen.estadoGeneral / 2)  // 0-10 → 1-5
                            : null
                    } />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
                        {analisisIA.resumen?.valoracionEstado && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Valoración del estado</p>
                                <p className="text-sm text-gray-800">{analisisIA.resumen.valoracionEstado}</p>
                            </div>
                        )}
                        {analisisIA.resumen?.dificultadYTiempo && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dificultad y tiempo estimado</p>
                                <p className="text-sm text-gray-800">{analisisIA.resumen.dificultadYTiempo}</p>
                            </div>
                        )}
                        {analisisIA.resumen?.potencialMejora && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Potencial de mejora</p>
                                <p className="text-sm text-gray-800">{analisisIA.resumen.potencialMejora}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══ STAGE 2: La reforma ══ */}
            <StageDivider number={2} title="La reforma" subtitle="Qué invertirías y qué retorno esperar" />

            {/* Tabla de escenarios */}
            {escenariosConReforma.some(e => e.venta) && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Escenarios de reforma</h3>
                        {hasIA && <IAToggleButton active={soloIA} onToggle={iaToggle} />}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-left py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Tipo</th>
                                    <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Coste reforma</th>
                                    <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Valor proyectado</th>
                                    <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Beneficio neto</th>
                                    <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Ratio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {escenariosConReforma.filter(e => e.venta).map((esc, idx) => {
                                    const orig = origByTipo[esc.tipoReforma];
                                    const valorProyectado = esc.venta.precioVentaEstimado;
                                    const beneficioNeto = valorProyectado - precio - esc.costoReforma;
                                    const incremento = precioSinReforma != null ? valorProyectado - precioSinReforma : null;
                                    const ratio = incremento != null && esc.costoReforma > 0
                                        ? Math.round(incremento / esc.costoReforma * 10) / 10 : null;
                                    const isRecomendado = mejorEscenario?.tipoReforma === esc.tipoReforma;
                                    const ratioColor = ratio > 1.5 ? "bg-green-50 text-green-700" : ratio >= 1.0 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
                                    const iaChanged = soloIA && hasIA && orig && orig.costoReforma !== esc.costoReforma;
                                    const origBeneficio = iaChanged ? valorProyectado - precio - orig.costoReforma : null;
                                    const origRatio = iaChanged && incremento != null && orig.costoReforma > 0
                                        ? Math.round(incremento / orig.costoReforma * 10) / 10 : null;
                                    return (
                                        <tr key={idx} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${isRecomendado ? "bg-green-50" : ""}`}>
                                            <td className="py-3 px-4 text-gray-900">
                                                {TIPO_LABELS[esc.tipoReforma] || esc.tipoReforma}
                                                {isRecomendado && <span className="ml-2 text-xs text-green-600 font-medium">Recomendado</span>}
                                            </td>
                                            <td className="py-3 px-4 text-right font-medium text-gray-900">
                                                {iaChanged ? (
                                                    <><span className="line-through text-gray-400 mr-1">{formatCurrency(orig.costoReforma)}</span><span className="text-blue-700">{formatCurrency(esc.costoReforma)}</span></>
                                                ) : formatCurrency(esc.costoReforma)}
                                            </td>
                                            <td className="py-3 px-4 text-right font-medium text-gray-900">{formatCurrency(valorProyectado)}</td>
                                            <td className={`py-3 px-4 text-right font-semibold ${beneficioNeto >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {iaChanged && origBeneficio != null ? (
                                                    <><span className="line-through text-gray-400 mr-1 font-normal">{origBeneficio >= 0 ? "+" : ""}{formatCurrency(origBeneficio)}</span><span>{beneficioNeto >= 0 ? "+" : ""}{formatCurrency(beneficioNeto)}</span></>
                                                ) : <>{beneficioNeto >= 0 ? "+" : ""}{formatCurrency(beneficioNeto)}</>}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                {ratio != null ? (
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ratioColor}`}>
                                                        {iaChanged && origRatio != null && <span className="line-through text-gray-400 mr-1 font-normal">{origRatio}x</span>}
                                                        {ratio}x
                                                    </span>
                                                ) : <span className="text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <p className="text-xs text-gray-400 mt-3">
                            Beneficio neto = valor de mercado estimado − precio de compra − coste de reforma. Ratio = incremento de valor / coste reforma.
                        </p>
                    </div>
                </div>
            )}

            {/* Detalle de actuaciones */}
            {propData.reformas?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Detalle de actuaciones</h3>
                        {hasIA && <IAToggleButton active={soloIA} onToggle={iaToggle} />}
                    </div>
                    <PropertyReformDetails
                        propiedad={propData}
                        modoComparacion={false}
                        setModoComparacion={() => {}}
                        indicadores={null}
                        soloIA={soloIA}
                        existenReformasIA={analisisIA !== null}
                    />
                </div>
            )}

            {/* ¿Qué actuaciones aportan más valor? */}
            {reformaParcialROI?.length > 0 && (
                <div id="reforma-parcial" className="bg-white rounded-xl border border-gray-200 p-6 mb-8 scroll-mt-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">¿Qué actuaciones aportan más valor?</h3>
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr>
                                <th className="text-left py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Actuación</th>
                                <th className="text-left py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Reforma</th>
                                <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Coste</th>
                                <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">Valor atribuido</th>
                                <th className="text-right py-3 px-4 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide font-medium">ROI</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(roiExpanded ? reformaParcialROI : reformaParcialROI.slice(0, 3)).map((act, idx) => (
                                <tr key={idx} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                    <td className="py-3 px-4 text-gray-900">
                                        <div className="flex items-center gap-1.5">
                                            {act.actuacion}
                                            {getExplicacionActuacion(act.actuacion) && (
                                                <div className="relative group/tip flex-shrink-0">
                                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                                                    <div className="invisible group-hover/tip:visible opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 absolute left-6 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-4 py-3 rounded-lg shadow-xl z-50 w-80 text-left leading-relaxed pointer-events-none">
                                                        {getExplicacionActuacion(act.actuacion)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-gray-500 text-xs">
                                        {act.tipoReforma}{act.constructor && ` · ${act.constructor}`}
                                    </td>
                                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900 tabular-nums">{formatCurrency(act.coste)}</td>
                                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900 tabular-nums">{formatCurrency(act.valorAtribuido)}</td>
                                    <td className={`py-3 px-4 text-right font-semibold tabular-nums ${act.roi > 50 ? "text-green-600" : act.roi >= 0 ? "text-amber-600" : "text-red-600"}`}>
                                        {act.roi > 0 ? "+" : ""}{act.roi}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {reformaParcialROI.length > 3 && (
                        <button
                            onClick={() => setRoiExpanded(v => !v)}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                            {roiExpanded ? "Mostrar menos ↑" : `Mostrar ${reformaParcialROI.length - 3} más ↓`}
                        </button>
                    )}
                </div>
            )}

            {/* ══ STAGE 3: Indicadores de inversión ══ */}
            <StageDivider number={3} title="Indicadores de inversión" subtitle="ROI, rentabilidad y retorno por escenario" />

            {/* ¿Vender o alquilar? */}
            {escenariosActivos.some(e => e.venta || e.alquiler?.alquilerMensualEstimado > 0) && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">¿Vender o alquilar?</h3>
                        {hasIA && <IAToggleButton active={soloIA} onToggle={iaToggle} />}
                    </div>
                    <div className="space-y-4">
                        {escenariosActivos
                            .filter(e => e.venta || e.alquiler?.alquilerMensualEstimado > 0)
                            .map((esc, idx) => {
                                const orig = origByTipo[esc.tipoReforma];
                                const iaChanged = soloIA && hasIA && orig && orig.costoReforma !== esc.costoReforma;
                                const gananciaVenta = esc.venta
                                    ? esc.venta.precioVentaEstimado - precio - esc.costoReforma : null;
                                const origGanancia = iaChanged && orig?.venta
                                    ? orig.venta.precioVentaEstimado - precio - orig.costoReforma : null;
                                const alquilerAnual = esc.alquiler?.alquilerMensualEstimado
                                    ? esc.alquiler.alquilerMensualEstimado * 12 : null;
                                const ventaWins = gananciaVenta != null && alquilerAnual != null && gananciaVenta > alquilerAnual * 5;
                                return (
                                    <div key={idx}>
                                        <p className="font-semibold text-gray-900 mb-3">{TIPO_LABELS[esc.tipoReforma] || esc.tipoReforma}</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {/* Venta */}
                                            <div className={`rounded-xl p-5 border ${ventaWins ? "border-2 border-blue-500" : "border-gray-200"}`}>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Venta</p>
                                                    {ventaWins && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">Mejor opción</span>}
                                                </div>
                                                {esc.venta ? (
                                                    <div className="space-y-2">
                                                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(esc.venta.precioVentaEstimado)}</p>
                                                        {gananciaVenta != null && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500 uppercase">Beneficio neto</span>
                                                                <span className={`text-sm font-medium ${gananciaVenta >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                                    {iaChanged && origGanancia != null && <span className="line-through text-gray-400 mr-1 font-normal">{origGanancia >= 0 ? "+" : ""}{formatCurrency(origGanancia)}</span>}
                                                                    {gananciaVenta >= 0 ? "+" : ""}{formatCurrency(gananciaVenta)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {esc.venta.roi.anio1 != null && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500 uppercase">ROI 1 año</span>
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {iaChanged && orig?.venta?.roi?.anio1 != null && <span className="line-through text-gray-400 mr-1 font-normal">{orig.venta.roi.anio1}%</span>}
                                                                    {esc.venta.roi.anio1}%
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : <p className="text-sm text-gray-400">Sin datos</p>}
                                            </div>
                                            {/* Alquiler */}
                                            <div className={`rounded-xl p-5 border ${!ventaWins && alquilerAnual != null ? "border-2 border-green-500" : "border-gray-200"}`}>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Alquiler</p>
                                                    {!ventaWins && alquilerAnual != null && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">Mejor opción</span>}
                                                </div>
                                                {esc.alquiler?.alquilerMensualEstimado > 0 ? (
                                                    <div className="space-y-2">
                                                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(esc.alquiler.alquilerMensualEstimado)}/mes</p>
                                                        {esc.alquiler.rentabilidadBruta != null && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500 uppercase">Rentabilidad</span>
                                                                <span className="text-sm font-medium text-green-600">
                                                                    {iaChanged && orig?.alquiler?.rentabilidadBruta != null && <span className="line-through text-gray-400 mr-1 font-normal">{orig.alquiler.rentabilidadBruta}%</span>}
                                                                    {esc.alquiler.rentabilidadBruta}% anual
                                                                </span>
                                                            </div>
                                                        )}
                                                        {esc.alquiler.tiempoRetornoMeses > 0 && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500 uppercase">Retorno</span>
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {iaChanged && orig?.alquiler?.tiempoRetornoMeses > 0 && <span className="line-through text-gray-400 mr-1 font-normal">{orig.alquiler.tiempoRetornoMeses} m</span>}
                                                                    {esc.alquiler.tiempoRetornoMeses} meses
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : <p className="text-sm text-gray-400">Sin datos</p>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Indicadores de Venta — ROI por horizonte temporal */}
            {escenariosConReforma.some(e => e.venta) && (
                <div id="roi-venta" className="bg-white rounded-xl border border-gray-200 p-6 mb-8 scroll-mt-20">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Indicadores de Venta</h3>
                        {hasIA && <IAToggleButton active={soloIA} onToggle={iaToggle} />}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {escenariosConReforma.filter(e => e.venta).map((esc, idx) => {
                            const orig = origByTipo[esc.tipoReforma];
                            const iaChanged = soloIA && hasIA && orig && orig.costoReforma !== esc.costoReforma;
                            const roi1 = esc.venta.roi.anio1;
                            const roiColor = roi1 > 10 ? "bg-green-50 text-green-700" : roi1 >= 5 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
                            const beneficioNeto = esc.venta.precioVentaEstimado - precio - esc.costoReforma;
                            const origBeneficio = iaChanged && orig?.venta ? orig.venta.precioVentaEstimado - precio - orig.costoReforma : null;
                            const roiItems = [
                                { label: '1A',  val: esc.venta.roi.anio1,  origVal: orig?.venta?.roi?.anio1 },
                                { label: '3A',  val: esc.venta.roi.anio3,  origVal: orig?.venta?.roi?.anio3 },
                                { label: '5A',  val: esc.venta.roi.anio5,  origVal: orig?.venta?.roi?.anio5 },
                                { label: '10A', val: esc.venta.roi.anio10, origVal: orig?.venta?.roi?.anio10 },
                            ].filter(r => r.val != null);
                            return (
                                <div key={idx} className="rounded-xl border border-gray-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="font-semibold text-gray-900">{TIPO_LABELS[esc.tipoReforma] || esc.tipoReforma}</p>
                                        {roi1 != null && <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${roiColor}`}>
                                            {iaChanged && orig?.venta?.roi?.anio1 != null && <span className="line-through mr-1 font-normal opacity-60">{orig.venta.roi.anio1}%</span>}
                                            ROI 1A: {roi1}%
                                        </span>}
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Coste reforma</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">
                                                {iaChanged ? (
                                                    <><span className="line-through text-gray-400 mr-1">{formatCurrency(orig.costoReforma)}</span><span className="text-blue-700">{formatCurrency(esc.costoReforma)}</span></>
                                                ) : formatCurrency(esc.costoReforma)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Precio venta est.</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{formatCurrency(esc.venta.precioVentaEstimado)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Beneficio neto</p>
                                            <p className={`text-sm font-medium mt-1 ${beneficioNeto >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {iaChanged && origBeneficio != null && <span className="line-through text-gray-400 mr-1 font-normal">{origBeneficio >= 0 ? "+" : ""}{formatCurrency(origBeneficio)}</span>}
                                                {beneficioNeto >= 0 ? "+" : ""}{formatCurrency(beneficioNeto)}
                                            </p>
                                        </div>
                                    </div>
                                    {roiItems.length > 0 && (
                                        <div className={`grid grid-cols-${roiItems.length} gap-2`}>
                                            {roiItems.map(r => (
                                                <div key={r.label} className="bg-gray-50 rounded-lg p-2 text-center">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">ROI {r.label}</p>
                                                    <p className={`text-sm font-semibold mt-0.5 ${r.val >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                        {iaChanged && r.origVal != null && r.origVal !== r.val && <span className="line-through text-gray-400 mr-1 text-xs font-normal">{r.origVal >= 0 ? "+" : ""}{r.origVal}%</span>}
                                                        {r.val >= 0 ? "+" : ""}{r.val}%
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Indicadores de Alquiler */}
            {escenariosConReforma.some(e => e.alquiler?.alquilerMensualEstimado > 0) && (
                <div id="rentabilidad-alquiler" className="bg-white rounded-xl border border-gray-200 p-6 mb-8 scroll-mt-20">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Indicadores de Alquiler</h3>
                        {hasIA && <IAToggleButton active={soloIA} onToggle={iaToggle} />}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {escenariosConReforma.filter(e => e.alquiler?.alquilerMensualEstimado > 0).map((esc, idx) => {
                            const orig = origByTipo[esc.tipoReforma];
                            const iaChanged = soloIA && hasIA && orig && orig.costoReforma !== esc.costoReforma;
                            const rentabilidad = esc.alquiler.rentabilidadBruta;
                            const rentColor = rentabilidad > 7 ? "bg-green-50 text-green-700" : rentabilidad >= 4 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
                            return (
                                <div key={idx} className="rounded-xl border border-gray-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="font-semibold text-gray-900">{TIPO_LABELS[esc.tipoReforma] || esc.tipoReforma}</p>
                                        {rentabilidad != null && <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${rentColor}`}>
                                            {iaChanged && orig?.alquiler?.rentabilidadBruta != null && <span className="line-through mr-1 font-normal opacity-60">{orig.alquiler.rentabilidadBruta}%</span>}
                                            {rentabilidad}%
                                        </span>}
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Coste reforma</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">
                                                {iaChanged ? (
                                                    <><span className="line-through text-gray-400 mr-1">{formatCurrency(orig.costoReforma)}</span><span className="text-blue-700">{formatCurrency(esc.costoReforma)}</span></>
                                                ) : formatCurrency(esc.costoReforma)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Alquiler/mes</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{formatCurrency(esc.alquiler.alquilerMensualEstimado)}/mes</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Rentabilidad</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">
                                                {iaChanged && orig?.alquiler?.rentabilidadBruta != null && <span className="line-through text-gray-400 mr-1 text-xs">{orig.alquiler.rentabilidadBruta}%</span>}
                                                {rentabilidad != null ? `${rentabilidad}% anual` : "N/D"}
                                            </p>
                                        </div>
                                    </div>
                                    {esc.alquiler.tiempoRetornoMeses > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-2">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">Tiempo de retorno</p>
                                            <p className="text-sm font-semibold text-gray-900 mt-0.5">
                                                {iaChanged && orig?.alquiler?.tiempoRetornoMeses > 0 && <span className="line-through text-gray-400 mr-1 font-normal">{orig.alquiler.tiempoRetornoMeses} m</span>}
                                                {esc.alquiler.tiempoRetornoMeses} meses
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Galería de imágenes */}
            {imagenes.length > 0 ? (
                <div className="mt-10 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Imágenes</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {imagenes.map((img, idx) => (
                            <div
                                key={img.id || idx}
                                className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100"
                            >
                                <img
                                    src={img.url_imagen}
                                    alt={`Imagen ${idx + 1}`}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                    loading="lazy"
                                    onClick={() => setLightboxUrl(img.url_imagen)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ) : propData.imagen_url ? (
                <div className="mt-10 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Imagen</h3>
                    <div className="aspect-[16/9] rounded-xl overflow-hidden bg-gray-100 max-w-lg">
                        <img src={propData.imagen_url} alt="Propiedad" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                </div>
            ) : null}

            {/* Lightbox */}
            {lightboxUrl && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setLightboxUrl(null)}
                >
                    <img
                        src={lightboxUrl}
                        className="max-h-[90vh] max-w-full rounded-lg shadow-2xl object-contain"
                        onClick={e => e.stopPropagation()}
                        alt="Imagen ampliada"
                    />
                    <button
                        className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300"
                        onClick={() => setLightboxUrl(null)}
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────

const VerdictCard = ({ precio, mejorEscenario, precioSinReforma }) => {
    if (!mejorEscenario?.venta) return (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Tu propiedad hoy</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(precio)}</p>
        </div>
    );

    const inversion = mejorEscenario.costoReforma;
    const valorDespues = mejorEscenario.venta.precioVentaEstimado;
    const ganancia = valorDespues - precio - inversion;
    const incremento = precioSinReforma != null ? valorDespues - precioSinReforma : null;
    const ratio = incremento != null && inversion > 0
        ? Math.round(incremento / inversion * 10) / 10 : null;
    const ratioColor = ratio > 1.5 ? "bg-green-50 text-green-700" : ratio >= 1.0 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";

    return (
        <div className="bg-white rounded-xl border border-gray-200 border-l-4 border-l-blue-600 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div className="text-center md:text-left">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Tu propiedad hoy</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(precio)}</p>
                </div>
                <div className="text-center">
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-300 hidden md:inline">›</span>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Inversión reforma</p>
                        <span className="text-gray-300 hidden md:inline">›</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(inversion)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{TIPO_LABELS[mejorEscenario.tipoReforma] || mejorEscenario.tipoReforma}</p>
                </div>
                <div className="text-center md:text-right">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Valor proyectado</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(valorDespues)}</p>
                </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-sm">
                <span className={`font-semibold ${ganancia >= 0 ? "text-green-600" : "text-red-600"}`}>
                    Beneficio neto: {ganancia >= 0 ? "+" : ""}{formatCurrency(ganancia)}
                </span>
                {ratio != null && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ratioColor}`}>Ratio {ratio}x</span>
                )}
                {mejorEscenario.venta.roi.anio1 != null && (
                    <span className="text-gray-500">ROI 1 año: {mejorEscenario.venta.roi.anio1.toFixed(1)}%</span>
                )}
            </div>
        </div>
    );
};

const StageDivider = ({ number, title, subtitle }) => (
    <div className="flex items-center gap-4 mb-8 mt-12">
        <div className="flex-1 h-px bg-gray-200" />
        <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {number}
            </div>
            <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
        </div>
        <div className="flex-1 h-px bg-gray-200" />
    </div>
);

const DetailItem = ({ label, value }) => (
    <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
    </div>
);


export default PropertyReport;
